import { z } from 'zod';
import { PRODUCT_TYPE_SCHEMA } from '../product-type/product-types.ts';
import { DISPOSAL_PURPOSE_SCHEMA } from '../product-channel/disposal-purpose.ts';
import { FREIGHT_TYPE_SCHEMA } from '../product-channel/freight-type.ts';

export const AbstractCustomQuotePayloadSchema = z.object({
    name: z.string(),
});

export const DisposalCustomQuotePayloadSchema = z.object({
    disposalPurpose: DISPOSAL_PURPOSE_SCHEMA.optional(),
});

export const CustomTransportQuotePayloadSchema = z.discriminatedUnion('freightType', [
    z.object({
        freightType: FREIGHT_TYPE_SCHEMA.exclude(['NON_HAZARDOUS_WASTE', 'HAZARDOUS_WASTE']).optional(),
    }),
    z.object({
        freightType: FREIGHT_TYPE_SCHEMA.extract(['NON_HAZARDOUS_WASTE', 'HAZARDOUS_WASTE']),
        disposalPurpose: DISPOSAL_PURPOSE_SCHEMA.optional(),
    }),
]);

export const RequiredDisposalCustomQuotePayloadSchema = z.object({
    disposalPurpose: DISPOSAL_PURPOSE_SCHEMA,
});

export const RequiredCustomTransportQuotePayloadSchema = z.discriminatedUnion('freightType', [
    z.object({
        freightType: FREIGHT_TYPE_SCHEMA.exclude(['NON_HAZARDOUS_WASTE', 'HAZARDOUS_WASTE']),
    }),
    z.object({
        freightType: FREIGHT_TYPE_SCHEMA.extract(['NON_HAZARDOUS_WASTE', 'HAZARDOUS_WASTE']),
        disposalPurpose: DISPOSAL_PURPOSE_SCHEMA,
    }),
]);

export const CUSTOM_TRANSPORT_PRODUCT_TYPES_SCHEMA = PRODUCT_TYPE_SCHEMA.extract(['CUSTOM_TRANSPORT']);

export const CUSTOM_WASTE_DISPOSAL_PRODUCT_TYPES_SCHEMA = PRODUCT_TYPE_SCHEMA.extract([
    'CUSTOM_NON_HAZARDOUS_WASTE_DISPOSAL',
    'CUSTOM_HAZARDOUS_WASTE_DISPOSAL',
]);

export const CUSTOM_WASTE_RECYCLING_PRODUCT_TYPES_SCHEMA = PRODUCT_TYPE_SCHEMA.extract([
    'CUSTOM_HAZARDOUS_WASTE_RECYCLING',
    'CUSTOM_NON_HAZARDOUS_WASTE_RECYCLING',
]);

export const CUSTOM_BULK_MATERIAL_PRODUCT_TYPES_SCHEMA = PRODUCT_TYPE_SCHEMA.extract(['CUSTOM_BULK_MATERIAL']);

export const CUSTOM_RECYCLED_BULK_MATERIAL_PRODUCT_TYPES_SCHEMA = PRODUCT_TYPE_SCHEMA.extract([
    'CUSTOM_RECYCLED_BULK_MATERIAL',
]);

export const FullCustomDisposalPayloadSchema = DisposalCustomQuotePayloadSchema.merge(AbstractCustomQuotePayloadSchema);
export const FullCustomTransportPayloadSchema = CustomTransportQuotePayloadSchema.and(AbstractCustomQuotePayloadSchema);

export const CustomisedQuotePayloadByProductTypeSchema = z.discriminatedUnion('productType', [
    z.object({
        productType: PRODUCT_TYPE_SCHEMA.exclude([
            ...CUSTOM_TRANSPORT_PRODUCT_TYPES_SCHEMA.options,
            ...CUSTOM_WASTE_DISPOSAL_PRODUCT_TYPES_SCHEMA.options,
            ...CUSTOM_WASTE_RECYCLING_PRODUCT_TYPES_SCHEMA.options,
        ]),
        payload: AbstractCustomQuotePayloadSchema,
    }),
    z.object({
        productType: CUSTOM_WASTE_RECYCLING_PRODUCT_TYPES_SCHEMA,
        payload: FullCustomDisposalPayloadSchema,
    }),
    z.object({
        productType: CUSTOM_WASTE_DISPOSAL_PRODUCT_TYPES_SCHEMA,
        payload: FullCustomDisposalPayloadSchema,
    }),
    z.object({
        productType: CUSTOM_TRANSPORT_PRODUCT_TYPES_SCHEMA,
        payload: FullCustomTransportPayloadSchema,
    }),
]);

export type QuotePayloads =
    | z.input<typeof AbstractCustomQuotePayloadSchema>
    | z.input<typeof FullCustomDisposalPayloadSchema>
    | z.input<typeof FullCustomTransportPayloadSchema>;

export const PartialCustomisedQuotePayloadByProductTypeSchema = z.discriminatedUnion('productType', [
    z.object({
        productType: PRODUCT_TYPE_SCHEMA.exclude([
            ...CUSTOM_TRANSPORT_PRODUCT_TYPES_SCHEMA.options,
            ...CUSTOM_WASTE_DISPOSAL_PRODUCT_TYPES_SCHEMA.options,
            ...CUSTOM_WASTE_RECYCLING_PRODUCT_TYPES_SCHEMA.options,
        ]),
        payload: AbstractCustomQuotePayloadSchema.partial().optional(),
    }),
    z.object({
        productType: CUSTOM_WASTE_RECYCLING_PRODUCT_TYPES_SCHEMA,
        payload: DisposalCustomQuotePayloadSchema.partial()
            .merge(AbstractCustomQuotePayloadSchema.partial())
            .optional(),
    }),
    z.object({
        productType: CUSTOM_WASTE_DISPOSAL_PRODUCT_TYPES_SCHEMA,
        payload: DisposalCustomQuotePayloadSchema.partial()
            .merge(AbstractCustomQuotePayloadSchema.partial())
            .optional(),
    }),
    z.object({
        productType: CUSTOM_TRANSPORT_PRODUCT_TYPES_SCHEMA,
        payload: CustomTransportQuotePayloadSchema.and(AbstractCustomQuotePayloadSchema.partial()).optional(),
    }),
]);
